/**
 * Supplier advisory-signal enum + display metadata for the Suppliers-Club
 * Daily Orders page. A supplier's per-item action ("معايا" reserve / "نواقص"
 * out-of-stock) is recorded as one of these signals; the ABSENCE of a signal
 * (null) is the "unanswered" state.
 *
 * ── WIRE ENCODING (confirmed by the delivered BE slice) ──────────────────────
 * The delivered contract (../backend/_bmad-output/sync/briefs/
 * 2026-07-05-supplier-daily-orders-delivered.md, and the source enum
 * ../backend/app/Enums/Commerce/SupplierSignal.php) ships INT-backed values:
 * `1 RESERVED · 2 PARTIALLY_RESERVED · 3 OOS_FLAGGED`, absence (null) = no
 * signal. The `signal` attribute on both the box-item read and the write echo
 * is that int. These constants mirror the backend ints; this file is the single
 * source of truth for the mapping on the FE side.
 *
 * v1 UI never *sends* PARTIALLY_RESERVED (the reserve control always reserves the
 * full remaining qty — design spec Section 6 / FE plan D-Δ3), but the enum still models
 * it so a server-returned partial reservation renders correctly (`reservedQty`
 * carries `nn/NN`). Treat RESERVED and PARTIALLY_RESERVED as the same "reserved"
 * visual family — see `isReservedSignal`.
 *
 * Meta shape mirrors OrderBoxStatus.enum.js: `{ i18nKey, color, icon }`.
 *   - i18nKey is a FULL key (not a prefix) → t(meta.i18nKey) yields the label.
 *   - color uses Framework7 named colors only.
 *   - icon is a Framework7 icon name (f7-icon `f7=` value).
 * Every i18nKey below points at a string that already exists in
 * src/i18n/locales/{ar,en}/supplier.json (design spec Section 12) — no new copy.
 */

export const SupplierSignal = {
  RESERVED: 1,
  PARTIALLY_RESERVED: 2,
  OOS_FLAGGED: 3,
};

/**
 * Backend SupplierBoxState int values — the advisory box-lifecycle pill
 * (../backend/app/Enums/Commerce/SupplierBoxState.php):
 *   1 AWAITING_PREP · 2 IN_COLLECTION · 3 RECEIVED · 4 CLOSED.
 * FE SSOT for the two lifecycle ints this feature actually reasons about:
 * IN_COLLECTION (poll-arming, useSupplierDailyOrders) and the RECEIVED threshold
 * (box_closed derivation, `deriveFrozenReason` below). Box lifecycle is NOT
 * surfaced in the UI, so no display metadata is modeled here.
 */
export const SupplierBoxState = {
  AWAITING_PREP: 1,
  IN_COLLECTION: 2,
  RECEIVED: 3,
  CLOSED: 4,
};

export const SupplierSignalMeta = {
  [SupplierSignal.RESERVED]: {
    i18nKey: 'supplier.claim.taken', // «محجوز» (REUSED, design spec Section 12)
    color: 'green',
    icon: 'checkmark_alt_circle_fill',
  },
  [SupplierSignal.PARTIALLY_RESERVED]: {
    i18nKey: 'supplier.claim.taken', // same "reserved" label; nn/NN comes from reservedQty
    color: 'green',
    icon: 'checkmark_alt_circle',
  },
  [SupplierSignal.OOS_FLAGGED]: {
    i18nKey: 'supplier.dailyOrders.stats.oos', // «نواقص»
    color: 'red',
    icon: 'xmark_circle_fill',
  },
};

/**
 * True when a signal represents a reservation of any kind (full or partial).
 * The single classifier the summary bar + card use so RESERVED vs
 * PARTIALLY_RESERVED never has to be spelled out at call sites.
 */
export function isReservedSignal(signal) {
  return signal === SupplierSignal.RESERVED || signal === SupplierSignal.PARTIALLY_RESERVED;
}

/** True when a signal is the out-of-stock flag. */
export function isOosSignal(signal) {
  return signal === SupplierSignal.OOS_FLAGGED;
}

/** True when the supplier has answered this item (any non-null signal). */
export function isAnsweredSignal(signal) {
  return isReservedSignal(signal) || isOosSignal(signal);
}

/**
 * Machine enum for WHY an item's controls are frozen (design spec Section 5, FE→BE
 * ask). Absence/unknown → treat as a generic lock. Kept here (rather than a
 * separate file) because it is a tightly-coupled companion of SupplierSignal:
 * both describe the write-state of the same `supplier-box-items` resource.
 *
 * `item_not_pending` — the collector already started handling THIS item.
 * `box_closed`       — the whole collection day is closed.
 * `plan_not_confirmed` — likely unreachable for a *visible* item (a box is only
 *                        visible once its plan is CONFIRMED); defensive only.
 */
export const SupplierFrozenReason = {
  ITEM_NOT_PENDING: 'item_not_pending',
  BOX_CLOSED: 'box_closed',
  PLAN_NOT_CONFIRMED: 'plan_not_confirmed',
};

/**
 * frozenReason → the plain-language i18n key revealed when the "خلاص" lock chip
 * is tapped (design spec Section 5 / Section 12). FULL keys; all authored by poolit-voice and
 * present in supplier.json. Consumers fall back to `itemNotPending` (the
 * overwhelmingly likely cause) when the reason is absent/unknown.
 */
export const SupplierFrozenReasonMeta = {
  [SupplierFrozenReason.ITEM_NOT_PENDING]: 'supplier.dailyOrders.locked.reasons.itemNotPending',
  [SupplierFrozenReason.BOX_CLOSED]: 'supplier.dailyOrders.locked.reasons.boxClosed',
  [SupplierFrozenReason.PLAN_NOT_CONFIRMED]: 'supplier.dailyOrders.locked.reasons.planNotConfirmed',
};

/** Resolve a frozenReason (possibly null/unknown) to its i18n key, safely. */
export function frozenReasonI18nKey(reason) {
  return (
    SupplierFrozenReasonMeta[reason] ||
    SupplierFrozenReasonMeta[SupplierFrozenReason.ITEM_NOT_PENDING]
  );
}

/**
 * Derive WHY a line item's controls are frozen, CLIENT-SIDE, from the fields the
 * delivered BE slice actually ships — it never shipped a `frozenReason` attribute
 * (delivered brief, findings table). Inputs, all already on the box read:
 *   - `frozen`         (item-level bool) — the write gate; true once the item
 *                      left PENDING (collector started it, or it was substituted).
 *   - `writable`       (box-level bool)  — true iff the plan is CONFIRMED/IN_PROGRESS.
 *   - `lifecycleState` (box-level int, SupplierBoxState) — 1..4.
 *
 * The mapping is UNAMBIGUOUS given the SupplierBoxState invariants (backend enum:
 * IN_COLLECTION ⇒ ≥1 item still non-final; RECEIVED ⇒ EVERY item final):
 *   - `lifecycleState >= RECEIVED (3)` → the whole collection day is done/closed
 *     → `box_closed` (RECEIVED and CLOSED share the closed-day treatment — design
 *     spec Section 5 / the invoice-placement doc's `lifecycleState IN (RECEIVED, CLOSED)`).
 *   - else `writable === false` → the plan isn't CONFIRMED/IN_PROGRESS →
 *     `plan_not_confirmed` (defensive; "likely unreachable" for a visible item,
 *     design spec Section 5 — a box is only visible once its plan is CONFIRMED).
 *   - else (box still open, item individually frozen) → `item_not_pending`
 *     (the overwhelmingly common case — a collector picked up THIS item mid-run).
 *
 * A frozen item at lifecycleState 1/2 can only be `item_not_pending` (the box
 * is provably not closed at those states), so `box_closed` and `item_not_pending`
 * never collide on the same input — no ambiguity, no BE ask needed.
 *
 * @returns {string|null} a SupplierFrozenReason value, or null when NOT frozen.
 */
export function deriveFrozenReason({ frozen, writable, lifecycleState } = {}) {
  if (!frozen) return null;
  if (typeof lifecycleState === 'number' && lifecycleState >= SupplierBoxState.RECEIVED) {
    return SupplierFrozenReason.BOX_CLOSED;
  }
  if (writable === false) return SupplierFrozenReason.PLAN_NOT_CONFIRMED;
  // Absent/unknown box context falls here too → the overwhelmingly likely cause,
  // matching frozenReasonI18nKey's own itemNotPending default.
  return SupplierFrozenReason.ITEM_NOT_PENDING;
}
